import { apiBaseUrl } from '../account/authApi';
import type { Class, Project } from '../data/types';
import { PublishBundleError, type BundleArtifactsExtractor } from './bundle/types';
import type { PublishConfig } from './types';

/**
 * Frontend Publish-to-bryta.app API client.
 *
 * Builds the same artefacts the Download path produces, but instead
 * of zipping them locally we POST the manifest + base64-encoded
 * weights to the backend. The backend re-renders the HTML server-
 * side so we never trust a client-uploaded `index.html` as the page
 * served to viewers.
 */

export interface PublishApiInput {
  project: Pick<Project, 'id' | 'name' | 'kind'>;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
  config: PublishConfig;
  trainer: BundleArtifactsExtractor;
  /** Override the API base. Defaults to the same `apiBaseUrl()` every
   *  other client uses (`http://localhost:8000` in dev, set via
   *  `VITE_API_URL` for other deploys). Tests set this directly so
   *  the fetch mock is unambiguous. */
  apiBase?: string;
}

export interface PublishApiResponse {
  id: string;
  slug: string;
  title: string;
  version: number;
  /** Full share URL including host — e.g. https://bryta.app/p/abc12345/show-off-demo/ */
  url: string;
  /** Cumulative views the live page has had. 0 on a fresh publish. */
  viewCount?: number;
}

export async function publishToBryta(input: PublishApiInput): Promise<PublishApiResponse> {
  const { project, classes, config, trainer, apiBase } = input;
  const base = apiBase ?? apiBaseUrl();
  // Image, pose, and audio all flow through the same publish API —
  // the backend's HTML generator branches on `project.kind` to pick
  // the right runtime (camera + MobileNet, camera + MoveNet,
  // microphone + spectrogram CNN).
  if (project.kind !== 'image' && project.kind !== 'pose' && project.kind !== 'audio') {
    throw new PublishBundleError(
      'unsupported-kind',
      `Publishing isn't available for ${project.kind} projects yet — coming soon.`,
    );
  }
  if (!trainer.extractArtifacts) {
    throw new PublishBundleError('no-trained-model', 'Train your model first, then publish.');
  }

  const artifacts = await trainer.extractArtifacts({
    projectId: project.id,
    projectName: project.name,
    labels: classes.map((c) => ({ classId: c.id, name: c.name })),
  });

  const body = {
    manifest: {
      schemaVersion: 1 as const,
      project: { id: project.id, name: project.name, kind: project.kind },
      config,
      trainedLabels: artifacts.metadataJson.brytaLabels,
    },
    modelJson: artifacts.modelJson,
    weightsB64: bytesToBase64(artifacts.weightsBin),
  };

  const res = await fetch(`${base}/api/publish`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let message = `Publish failed (${res.status}).`;
    try {
      const json = (await res.json()) as { detail?: string };
      if (typeof json.detail === 'string' && json.detail) message = json.detail;
    } catch {
      // body wasn't JSON; keep the generic message
    }
    throw new PublishApiError(res.status, message);
  }

  return (await res.json()) as PublishApiResponse;
}

export class PublishApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'PublishApiError';
  }
}

export interface PublishedAppSummary {
  id: string;
  projectId: string;
  slug: string;
  title: string;
  version: number;
  url: string;
  /** Cumulative views the live page has had. */
  viewCount?: number;
  updatedAt: string;
}

/** GET /api/publish/me — list the signed-in kid's published apps so
 *  the studio can show "already published v3" + the share URL when
 *  they re-open the Publish view. Returns null on 401 (signed-out)
 *  rather than throwing — the publish view doesn't render this
 *  panel for signed-out kids anyway. */
export async function fetchMyPublishedApps(
  apiBase?: string,
): Promise<PublishedAppSummary[] | null> {
  const base = apiBase ?? apiBaseUrl();
  const res = await fetch(`${base}/api/publish/me`, {
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) {
    throw new PublishApiError(res.status, `Could not load your published apps (${res.status}).`);
  }
  const data = (await res.json()) as { apps: PublishedAppSummary[] };
  return data.apps;
}

/** DELETE /api/publish/{id} — tear down a published app the kid owns.
 *  Throws on 4xx / 5xx so the caller can show a real error. Idempotent
 *  in spirit: a 404 means the app is already gone (or never existed
 *  for this user), which the caller treats as success. */
export async function unpublishApp(appId: string, apiBase?: string): Promise<void> {
  const base = apiBase ?? apiBaseUrl();
  const res = await fetch(`${base}/api/publish/${encodeURIComponent(appId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (res.status === 204 || res.status === 404) return;
  let detail = `Could not unpublish (${res.status}).`;
  try {
    const data = (await res.json()) as { detail?: string };
    if (data?.detail) detail = data.detail;
  } catch {
    // ignore — keep the generic message
  }
  throw new PublishApiError(res.status, detail);
}

/** Convert a Uint8Array to base64 in a way that survives jsdom's
 *  TextEncoder quirks (passing bytes directly via btoa fails for
 *  non-ASCII byte values). Uses the standard chunk-then-btoa pattern. */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}
